import { useMutation } from '@tanstack/react-query'
import { ErrorWithBody, post, queryKeys, SignUpUrl } from '@mobi/account/Utils'
import {
  ApplicationRequest,
  ApplicationResponse,
  DriverLicenceDetails,
  PassportDetails,
} from '@mobi/api-types/index'
import dayjs from 'dayjs'
import { type DuplicateContactCheckResponse } from '../../PersonalDetails/Hooks'
import { useDispatch, useSelector } from 'react-redux'
import { getSignUpState, setSignupApplication, setSignupVerification } from '../Store'
import { useLocation, useNavigate } from 'react-router'
import { routes, trackSignUpError } from '../Utils'

type ApplicationSaveVariables = {
  driverLicenceDetails?: DriverLicenceDetails | null
  passportDetails?: PassportDetails | null
}

type UseApplicationOptions = {
  setValidationMessages: React.Dispatch<React.SetStateAction<Record<string, string>>>
  setShowErrorPopup: React.Dispatch<React.SetStateAction<boolean>>
  nextPageRoute?: string
}

export const useApplication = ({
  setValidationMessages,
  setShowErrorPopup,
  nextPageRoute,
}: UseApplicationOptions) => {
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const signUpState = useSelector(getSignUpState)
  const application = signUpState.signupApplication
  const verification = signUpState.signupVerification
  const riskAssessmentAnswers = signUpState.signupRiskAssessmentAnswerLookupKeys

  const { mutate: applicationSave, status: applicationSaveStatus } = useMutation<
    ApplicationResponse,
    ErrorWithBody<DuplicateContactCheckResponse>,
    ApplicationSaveVariables
  >({
    mutationKey: [queryKeys.signupApplicationSave],
    mutationFn: (dvs): Promise<ApplicationResponse> => {
      if (!application.residentialAddress) {
        throw new Error('Residential address is required')
      }

      const req: ApplicationRequest = {
        applicantDetails: {
          firstName: application.firstName,
          middleName: application.middleName,
          lastName: application.surname,
          firstNameAlias: application.firstNameAlias,
          lastNameAlias: application.lastNameAlias,
          birthdate: dayjs(application.birthdate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
          email: application.email,
          mobileNumber: application.mobileNumber,
          residentialAddress: application.residentialAddress,
          previousAddress: application.previousAddress,
        },
        applicantOptions: {
          marketingOptInIndicator: application.marketingOptInIndicator,
          isIdVerificationOnlyAccount: signUpState.isVerificationAccount,
        },
        applicantRiskDetails: riskAssessmentAnswers,
        driverLicenceDetails: dvs.driverLicenceDetails ?? undefined,
        passportDetails: dvs.passportDetails ?? undefined,
      }
      return post<
        ApplicationRequest,
        ApplicationResponse,
        ErrorWithBody<DuplicateContactCheckResponse>
      >(SignUpUrl.applicationSave(application.applicationReferenceNumber.toString()), req)
    },

    onSuccess: response => {
      dispatch(
        setSignupApplication({
          ...application,
          applicationReferenceNumber: Number(response.applicationReferenceNumber),
        })
      )
      dispatch(
        setSignupVerification({
          ...verification,
          duplicateExists: response.duplicateExists,
          isDuplicateClosed: response.isDuplicateClosed,
        })
      )
      if (response.duplicateExists) {
        navigate(routes.signUpUnsuccessful)
        return
      }
      if (nextPageRoute) {
        navigate(nextPageRoute)
        return
      }
    },

    onError: error => {
      if (error.status === 409 && error.body) {
        if (error.body.isDuplicateMobile) {
          trackSignUpError(pathname, 'Duplicate Mobile Number')
          setValidationMessages(prev => ({ ...prev, mobile: 'duplicate' }))
        }
        if (error.body.isDuplicateEmail) {
          trackSignUpError(pathname, 'Duplicate Email')
          setValidationMessages(prev => ({ ...prev, email: 'duplicate' }))
        }
        return
      }
      setShowErrorPopup(true)
      trackSignUpError(pathname, error.statusText)
    },
  })
  return { applicationSave, applicationSaveStatus }
}
